import * as React from 'react';
import { cn, truncateString } from '../../lib/utils';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Button } from './button/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import clsx from 'clsx';

export type Option = {
  label: string;
  value: string;
  short_tag?: string;
};

interface MultiSelectProps {
  options: Option[];
  selected: string[];
  onChange: (values: string[]) => void;
  className?: string;
  popoverClassName?: string;
  placeholder?: string;
  disabled?: boolean;
  chevronClassName?: string;
  singleSelect?: boolean;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  className,
  popoverClassName,
  chevronClassName,
  placeholder = 'Select options',
  disabled = false,
  singleSelect,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);

  const selectedItems = options.filter((option) =>
    selected.includes(option.value),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between bg-prompt-safecomwhite',
            selectedItems.length > 0 ? 'h-full' : 'h-10',
            className,
          )}
          disabled={disabled}
        >
          <div className="flex flex-wrap gap-1">
            {selectedItems.length > 0 ? (
              singleSelect ? (
                <span className="text-sm text-prompt-safecomvulcan">
                  {truncateString({
                    str: selectedItems[0].label,
                    maxLength: 30,
                  })}
                </span>
              ) : (
                selectedItems.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center gap-1 rounded bg-prompt-safecomgallery px-1 py-0.5"
                  >
                    <span className="text-sm text-prompt-safecomvulcan">
                      {truncateString({
                        str: option.label,
                        maxLength: 30,
                      })}
                    </span>
                    <button
                      className="ml-1 rounded-full outline-none focus:ring-2 focus:ring-prompt-safecomoutrageous-orange"
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange(
                          selected.filter((value) => value !== option.value),
                        );
                      }}
                    >
                      <X
                        className={cn(
                          'h-3 w-3 text-prompt-safecomabbey hover:text-prompt-safecomvulcan',
                        )}
                      />
                    </button>
                  </div>
                ))
              )
            ) : (
              <span className="text-sm text-prompt-safecomvulcan">
                {placeholder}
              </span>
            )}
          </div>

          <ChevronsUpDown
            className={cn('h-4 w-4 shrink-0 opacity-50', chevronClassName)}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={clsx('w-full p-0', popoverClassName)}>
        <Command className="w-full">
          <X
            size={18}
            className="absolute right-2 top-2.5 cursor-pointer opacity-50"
            onClick={() => setOpen(false)}
          />
          <CommandInput placeholder="Search..." className="h-9" />
          <CommandEmpty>No options found.</CommandEmpty>
          <CommandGroup className="max-h-64 overflow-auto">
            {options.map((option) => {
              return (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  className="group"
                  onSelect={() => {
                    if (singleSelect) {
                      onChange([option.value]);
                      setOpen(false);
                    } else {
                      onChange(
                        selected.includes(option.value)
                          ? selected.filter((value) => value !== option.value)
                          : [...selected, option.value],
                      );
                      setOpen(true);
                    }
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      selected.includes(option.value)
                        ? 'opacity-100'
                        : 'opacity-0',
                    )}
                  />
                  <div className="flex w-full justify-between text-sm">
                    <span>{option.label}</span>
                    {option?.short_tag && (
                      <span className="flex h-fit items-center rounded-full bg-muted px-2 py-[1px] text-[11px] text-muted-foreground group-hover:bg-popover">
                        {option?.short_tag}
                      </span>
                    )}
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
